import React, { useEffect, useState } from 'react';
import { Save, Loader2, ShieldCheck, Mail, KeyRound, CheckCircle2, AlertCircle } from 'lucide-react';
import { adminMe, adminChangePassword } from '../../lib/api';
import { toast } from '../../hooks/use-toast';

const SettingsTab = () => {
  const [me, setMe] = useState({ email: '', email_alerts: false });
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '', new_email: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminMe().then(setMe).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) return toast({ title: 'Password too short', description: 'Use at least 8 characters', variant: 'destructive' });
    if (form.new_password !== form.confirm) return toast({ title: 'Passwords do not match', variant: 'destructive' });
    setSaving(true);
    try {
      const body = { current_password: form.current_password, new_password: form.new_password };
      if (form.new_email && form.new_email !== me.email) body.new_email = form.new_email;
      const res = await adminChangePassword(body);
      localStorage.setItem('wyw_admin_token', res.token);
      localStorage.setItem('wyw_admin_email', res.email);
      setMe({ ...me, email: res.email });
      setForm({ current_password: '', new_password: '', confirm: '', new_email: '' });
      toast({ title: 'Password updated' });
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full bg-white border border-[#EFE3D2] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#C58B76] focus:ring-2 focus:ring-[#F4D5C5]';

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl border border-[#EFE3D2] p-6 md:p-8">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-[#C58B76]" />
          <h3 className="font-serif-display text-2xl text-[#2C2416]">Change admin credentials</h3>
        </div>
        <p className="text-sm text-[#5B4A3A] mb-6">Update your email or password. You'll stay signed in - your token refreshes automatically.</p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#7A5C4C] mb-2">Signed in as</label>
            <div className="flex items-center gap-2 bg-[#FAF6F1] rounded-lg px-4 py-3 text-sm text-[#2C2416]"><Mail className="w-4 h-4 text-[#C58B76]" /> {me.email || '—'}</div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-[#7A5C4C] mb-2">Current Password *</label>
            <input required type="password" className={inputCls} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#7A5C4C] mb-2">New Password *</label>
              <input required type="password" minLength={8} className={inputCls} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} placeholder="Min 8 characters" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#7A5C4C] mb-2">Confirm New Password *</label>
              <input required type="password" minLength={8} className={inputCls} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-[#7A5C4C] mb-2">Change email (optional)</label>
            <input type="email" className={inputCls} value={form.new_email} onChange={(e) => setForm({ ...form, new_email: e.target.value })} placeholder={me.email} />
          </div>
        </div>

        <button type="submit" disabled={saving} className="btn-primary rounded-full px-6 py-3 mt-6 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : <><Save className="w-4 h-4" /> Update credentials</>}
        </button>
      </form>

      <div className="bg-white rounded-2xl border border-[#EFE3D2] p-6 md:p-8">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-5 h-5 text-[#C58B76]" />
          <h3 className="font-serif-display text-xl text-[#2C2416]">Email alerts</h3>
        </div>
        {me.email_alerts ? (
          <div className="flex items-start gap-2 text-sm text-[#2C2416] bg-[#EAF2E1] border border-[#C8D5B9] rounded-lg p-3">
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3A4A2B]" />
            <div>
              <div className="font-medium">SendGrid is active</div>
              <div className="text-xs text-[#5B4A3A] mt-0.5">Every new enquiry is emailed to your team the moment it's submitted.</div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 text-sm text-[#2C2416] bg-[#FDECE1] border border-[#F4D5C5] rounded-lg p-3">
            <AlertCircle className="w-4 h-4 mt-0.5 text-[#C58B76]" />
            <div>
              <div className="font-medium">SendGrid not configured</div>
              <div className="text-xs text-[#5B4A3A] mt-0.5">Enquiries are still stored in the database. To enable email alerts, add these to <code className="bg-white px-1 py-0.5 rounded">backend/.env</code>:</div>
              <pre className="text-[11px] bg-[#2C2416] text-[#F4D5C5] p-3 rounded mt-2 overflow-x-auto">SENDGRID_API_KEY=SG.xxxxx
SENDGRID_FROM_EMAIL=noreply@wrapyourwish.com
SENDGRID_TO_EMAIL=reachus@wrapyourwish.com</pre>
              <div className="text-xs text-[#5B4A3A] mt-2">Restart the backend after adding the key.</div>
            </div>
          </div>
        )}

        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-[#7A5C4C]">Security tips</div>
          <ul className="text-sm text-[#2C2416] mt-2 space-y-2">
            <li className="flex gap-2"><KeyRound className="w-4 h-4 mt-0.5 text-[#C58B76]" /> Use at least 12 characters mixing letters, numbers &amp; symbols</li>
            <li className="flex gap-2"><KeyRound className="w-4 h-4 mt-0.5 text-[#C58B76]" /> Never share this password over WhatsApp or SMS</li>
            <li className="flex gap-2"><KeyRound className="w-4 h-4 mt-0.5 text-[#C58B76]" /> Rotate the JWT_SECRET in .env every 6 months</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
